import { alertClasses, createTheme, Fade, IconButton, type Shadows, type Theme } from "~/common/mui";
import {
  C_ERROR,
  C_ERROR_CONTAINER,
  C_INFORMATION,
  C_INFORMATION_CONTAINER,
  C_INVERSE_ON_SURFACE,
  C_INVERSE_PRIMARY,
  C_INVERSE_SURFACE,
  C_ON_ERROR,
  C_ON_ERROR_CONTAINER,
  C_ON_INFORMATION_CONTAINER,
  C_ON_PRIMARY,
  C_ON_PRIMARY_CONTAINER,
  C_ON_SALES_CAMPAIGN,
  C_ON_SECONDARY,
  C_ON_SECONDARY_CONTAINER,
  C_ON_SUCCESS_CONTAINER,
  C_ON_SURFACE,
  C_ON_SURFACE_LOW_EMPHASIS,
  C_ON_SURFACE_VARIANT,
  C_ON_WARNING_CONTAINER,
  C_OUTLINE,
  C_OUTLINE_VARIANT,
  C_PRIMARY,
  C_PRIMARY_CONTAINER,
  C_PRIMARY_HOVER,
  C_SALES_CAMPAIGN,
  C_SECONDARY,
  C_SECONDARY_CONTAINER,
  C_SUCCESS,
  C_SUCCESS_CONTAINER,
  C_SURFACE,
  C_SURFACE_BRIGHT,
  C_SURFACE_CONTAINER,
  C_SURFACE_CONTAINER_HIGH,
  C_SURFACE_CONTAINER_HIGHEST,
  C_SURFACE_CONTAINER_LOW,
  C_SURFACE_CONTAINER_LOWEST,
  C_SURFACE_DIM,
  C_TABLE_HOVER_BACKGROUND,
  C_WARNING,
  C_WARNING_CONTAINER,
  C_WHITE,
} from "~/common/components/icon/Colors";
import { FONT_RAW_SIZES, FontSize, getFontStyle } from "~/common/components/materialInput/Fonts";
import {
  RESOLUTION_DESKTOP_L,
  RESOLUTION_DESKTOP_M,
  RESOLUTION_DESKTOP_S,
  RESOLUTION_TABLET,
} from "~/common/utils/screenSizes";
import { BOTTOM_NAVIGATION_HEIGHT } from "~/features/BottomNavigation/BottomNavigationConstants";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { CORNER_EXTRA_LARGE, CORNER_EXTRA_SMALL, CORNER_MEDIUM } from "~/common/components/icon/Corners";
import {
  ELEVATION_0,
  ELEVATION_1,
  ELEVATION_2,
  ELEVATION_3,
  ELEVATION_4,
  ELEVATION_5,
} from "~/common/components/icon/Elevation";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { MENU_ITEM_PADDING } from "./themeConstants";

const BUTTON_SIZES = {
  SMALL: 34,
  MEDIUM: 40,
  LARGE: 48,
};
const BUTTON_INLINE_PADDING = 24;
const INPUT_SIZES = {
  SMALL: 40,
  MEDIUM: 56,
};
const BORDER_WIDTH = 1;
const TOTAL_BORDER_WIDTH = BORDER_WIDTH * 2;

const BUTTON_LINE_HEIGHT = FONT_RAW_SIZES[FontSize.LabelLarge].lineHeight;
const INPUT_LINE_HEIGHT = FONT_RAW_SIZES[FontSize.BodyLarge].lineHeight;

export const theme = createTheme({
  components: {
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: C_OUTLINE,
          padding: "8px",
        },
      },
    },
    MuiDateRangeCalendar: {
      styleOverrides: {
        root: {
          height: "100%",
          padding: "0 16px 24px 16px",
          "& .MuiDateRangeCalendar-monthContainer": {
            display: "flex",
            flexDirection: "column",
            "& .MuiPickersArrowSwitcher-root": {
              paddingTop: 0,
            },
          },

          "& .MuiDateRangeCalendar-monthContainer:not(:last-of-type)": {
            border: "none !important",
            marginRight: "24px",
          },
          "& .MuiDayCalendar-weekContainer": {
            margin: "4px 0",
          },
          "& .MuiDayCalendar-weekNumberLabel": {
            width: "50px",
            height: "40px",
            margin: "0",
            marginRight: "8px",
            whiteSpace: "nowrap",
            ...getFontStyle(FontSize.BodyLarge),
          },

          "& .MuiDayCalendar-weekNumber": {
            width: "50px",
            height: "40px",
            margin: "0",
            marginRight: "8px",
            whiteSpace: "nowrap",
            ...getFontStyle(FontSize.BodyLarge),
          },

          "& .MuiDayCalendar-root": {
            display: "flex",
            flexDirection: "column",
            flex: "1 0 auto",
          },

          "& .MuiDayCalendar-slideTransition": {
            flex: "1 0 auto",
            overflow: "hidden",
          },

          "& .MuiDayCalendar-weekDayLabel": {
            width: "40px",
            height: "40px",
            margin: "4px 2px",
            ...getFontStyle(FontSize.BodyLarge),
          },

          "& .MuiPickersDay-root": {
            width: "40px",
            height: "40px",
            ...getFontStyle(FontSize.BodyLarge),
          },
        },
      },
    },
    MuiPickersToolbar: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          padding: "24px 16px",
          "& .MuiPickersToolbar-title": {
            ...getFontStyle(FontSize.LabelLarge),
            color: theme.palette.text.label,
            textTransform: "unset",
          },
        }),
      },
    },
    MuiPickersToolbarText: {
      styleOverrides: {
        root: {
          ...getFontStyle(FontSize.TitleLarge),
        },
      },
    },
    MuiGrid: {
      defaultProps: {
        columnSpacing: "var(--gutter)",
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          position: "relative",
          border: "1px solid transparent",
          borderRadius: "8px",

          "&.MuiChip-assist": {
            borderColor: C_OUTLINE,
            backgroundColor: "transparent",
            "& .MuiChip-label": {
              color: C_ON_SURFACE_VARIANT,
            },
            "& .MuiChip-icon": {
              color: C_PRIMARY,
            },
            "& .MuiChip-deleteIcon": {
              color: C_ON_SURFACE_VARIANT,
            },
          },
          "&.MuiChip-filter": {
            "&.Mui-selected": {
              borderColor: C_SECONDARY_CONTAINER,
              backgroundColor: C_SECONDARY_CONTAINER,
              "& .MuiChip-label": {
                color: C_ON_SECONDARY_CONTAINER,
              },
              "& .MuiChip-icon": {
                color: C_ON_SECONDARY_CONTAINER,
              },
              "& .MuiChip-deleteIcon": {
                color: C_ON_SURFACE_VARIANT,
              },
            },
            "&:not(.Mui-selected)": {
              borderColor: C_OUTLINE,
              backgroundColor: "transparent",
              "& .MuiChip-label": {
                color: C_ON_SURFACE_VARIANT,
              },
              "& .MuiChip-icon": {
                color: C_PRIMARY,
              },
              "& .MuiChip-deleteIcon": {
                color: C_ON_SECONDARY_CONTAINER,
              },
            },
          },
          "&.MuiChip-input": {
            "&.Mui-selected": {
              borderColor: C_SECONDARY_CONTAINER,
              backgroundColor: C_SECONDARY_CONTAINER,
              "& .MuiChip-label": {
                color: C_ON_SECONDARY_CONTAINER,
              },
              "& .MuiChip-icon": {
                color: C_PRIMARY,
              },
              "& .MuiChip-deleteIcon": {
                color: C_ON_SECONDARY_CONTAINER,
              },
            },
            "&:not(.Mui-selected)": {
              borderColor: C_OUTLINE,
              backgroundColor: "transparent",
              "& .MuiChip-label": {
                color: C_ON_SURFACE_VARIANT,
              },
              "& .MuiChip-icon": {
                color: C_ON_SECONDARY_CONTAINER,
              },
              "& .MuiChip-deleteIcon": {
                color: C_ON_SURFACE_VARIANT,
              },
            },
          },
          "&.MuiChip-suggestion": {
            borderColor: C_ON_SURFACE_VARIANT,
            backgroundColor: "transparent",
            "& .MuiChip-label": {
              color: C_ON_SURFACE_VARIANT,
            },
            "& .MuiChip-icon": {
              color: C_PRIMARY,
            },
            "& .MuiChip-deleteIcon": {
              color: C_ON_SURFACE_VARIANT,
            },
          },
          "&:hover, &:focus": {
            "&::after": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: C_ON_SURFACE,
              opacity: 0.08,
              borderRadius: "inherit",
              pointerEvents: "none",
            },
          },
          "&:disabled": {
            opacity: 0.38,
          },
        },
        label: {
          ...getFontStyle(FontSize.LabelLarge),
          paddingInline: "16px",
        },
        icon: {
          marginLeft: "8px",
          marginRight: "-8px",
          fontSize: "18px",
        },
        deleteIcon: {
          marginLeft: "-8px",
          marginRight: "8px",
          fontSize: "18px",
        },
      },
    },
    MuiSlider: {
      styleOverrides: {
        root: {
          width: "calc(100% - 16px)",
          marginLeft: "8px",
          marginRight: "8px",
        },
        track: {
          height: "8px",
          borderRadius: "7px",
        },
        rail: {
          height: "8px",
          borderRadius: "7px",
        },
      },
    },
    MuiAccordion: {
      defaultProps: {
        elevation: 0,
        square: true,
        disableGutters: true,
        slots: {
          heading: "p",
        },
      },
      styleOverrides: {
        root: {
          position: "relative",
          ["&.Mui-expanded"]: {
            margin: 0,
            ["&:before"]: {
              opacity: 1,
            },

            ["& + &"]: {
              ["&:before"]: {
                display: "block",
              },
            },
          },
          "&:first-of-type": {
            "&::before": {
              display: "inherit",
            },
          },
          "&:last-of-type:not(.MuiCard-root)": {
            "&::after": {
              position: "absolute",
              content: '""',
              // display: "none",
              left: 0,
              right: 0,
              bottom: 0,
              borderBottom: `2px solid ${C_OUTLINE_VARIANT}`,
            },
          },
          ["&:before:not(.MuiCard-root)"]: {
            position: "absolute",
            content: '""',
            // display: "none",
            left: 0,
            right: 0,
            top: 0,
            borderBottom: `2px solid ${C_OUTLINE_VARIANT}`,
          },
        },
      },
    },
    MuiAccordionSummary: {
      defaultProps: {
        expandIcon: (
          <IconButton variant="contained" color={"secondary"} size={"tiny"} tabIndex={-1}>
            <ExpandMoreIcon />
          </IconButton>
        ),
      },
      styleOverrides: {
        root: {
          ...getFontStyle(FontSize.BodyLarge),
          fontWeight: "bold",
          padding: "0 16px",
        },
        content: {
          margin: "16px 0",
        },
        expandIconWrapper: {
          marginLeft: "8px",
        },
      },
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: {
          padding: "0 16px 16px",
          ".MuiDivider-root + &": {
            paddingTop: "16px",
          },
        },
      },
    },
    MuiAlertTitle: {
      styleOverrides: {
        root: {
          fontWeight: "bold",
          marginBottom: "4px",
        },
      },
    },
    MuiAlert: {
      defaultProps: {
        slotProps: {
          closeIcon: {
            fontSize: "medium",
          },
        },
      },
      styleOverrides: {
        root: {
          alignItems: "center",
          maxWidth: "760px",
          ...getFontStyle(FontSize.BodyMedium),
          a: {
            color: "inherit",
            fontWeight: 600,
          },
          padding: "8px 16px",
        },
        message: {
          margin: "-8px 0",
        },
        action: {
          alignItems: "center",
          marginTop: "-6px",
          marginBottom: "-6px",
          paddingTop: 0,
        },
        icon: {
          fontSize: "24px",
          alignItems: "center",
          padding: "0px",
        },
        standardInfo: ({ theme }) => ({
          border: `1px solid ${theme.palette.info.main}`,
          background: theme.palette.info.container,
          color: theme.palette.info.onContainer,
          [`& .${alertClasses.icon}`]: {
            color: theme.palette.info.main,
          },
        }),
        standardSuccess: ({ theme }) => ({
          border: `1px solid ${theme.palette.success.main}`,
          background: theme.palette.success.container,
          color: theme.palette.success.onContainer,
          [`& .${alertClasses.icon}`]: {
            color: theme.palette.success.onContainer,
          },
        }),
        standardError: ({ theme }) => ({
          border: `1px solid ${theme.palette.error.main}`,
          background: theme.palette.error.container,
          color: theme.palette.error.onContainer,
          [`& .${alertClasses.icon}`]: {
            color: theme.palette.error.main,
          },
        }),
        standardWarning: ({ theme }) => ({
          border: `1px solid ${theme.palette.warning.main}`,
          background: theme.palette.warning.container,
          color: theme.palette.warning.onContainer,
          [`& .${alertClasses.icon}`]: {
            color: theme.palette.warning.main,
          },
        }),
      },
    },
    MuiSnackbar: {
      defaultProps: {
        autoHideDuration: 5000,
        TransitionComponent: Fade,
        transitionDuration: { exit: 500, appear: 500, enter: 500 },
      },
      styleOverrides: {
        root: ({ theme }) => ({
          justifyContent: "center",
          bottom: `${BOTTOM_NAVIGATION_HEIGHT + 16}px`,
          [theme.breakpoints.up("desktop-s")]: {
            bottom: "24px",
          },
        }),
      },
    },
    MuiSnackbarContent: {
      styleOverrides: {
        root: {
          flexGrow: "0",
          color: C_INVERSE_ON_SURFACE,
          background: C_INVERSE_SURFACE,
          ...getFontStyle(FontSize.LabelLarge),
          maxWidth: "768px",
          button: {
            minWidth: "unset",
          },
        },
        message: {
          flex: "1 1 75%",
        },
        action: {
          flex: "0 1 fit-content",
        },
      },
    },
    MuiButtonBase: {
      styleOverrides: {
        root: {
          textTransform: "none",
        },
      },
    },
    MuiButton: {
      defaultProps: {
        variant: "contained",
        color: "primary",
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          textTransform: "none",
          ...getFontStyle(FontSize.LabelLarge),
          borderRadius: CORNER_EXTRA_LARGE,
          paddingInline: `${BUTTON_INLINE_PADDING}px`,
          paddingBlock: calculateButtonVerticalPadding(BUTTON_SIZES.MEDIUM, BUTTON_LINE_HEIGHT),
          variants: [
            {
              props: {
                variant: "contained",
                color: "primary",
                disabled: false,
              },
              style: {
                background: C_PRIMARY,
                color: C_ON_PRIMARY,

                ["&:hover"]: {
                  background: C_PRIMARY,
                  boxShadow: ELEVATION_1,
                },
              },
            },
            {
              props: {
                variant: "contained",
                color: "secondary",
                disabled: false,
              },
              style: {
                background: C_SECONDARY_CONTAINER,
                color: C_ON_SECONDARY_CONTAINER,

                ["&:hover"]: {
                  background: C_SECONDARY_CONTAINER,
                  boxShadow: ELEVATION_1,
                },
              },
            },

            {
              props: {
                variant: "contained",
                color: "error",
                disabled: false,
              },
              style: {
                background: C_ERROR_CONTAINER,
                color: C_ON_ERROR_CONTAINER,

                ["&:hover"]: {
                  background: C_ERROR_CONTAINER,
                  boxShadow: ELEVATION_1,
                },
              },
            },
            {
              props: {
                variant: "contained",
                disabled: true,
              },
              style: {
                background: convertHexToRGBA(C_ON_SURFACE, 0.12),
                color: convertHexToRGBA(C_ON_SURFACE, 0.38),
              },
            },
            {
              props: {
                variant: "outlined",
                disabled: false,
              },
              style: {
                border: `1px solid ${C_OUTLINE}`,
                paddingBlock: calculateButtonVerticalPadding(
                  BUTTON_SIZES.MEDIUM - TOTAL_BORDER_WIDTH,
                  BUTTON_LINE_HEIGHT,
                ),
                color: C_PRIMARY,
                background: "transparent",

                ["&:hover"]: {
                  background: convertHexToRGBA(C_PRIMARY, 0.08),
                  border: `1px solid ${C_OUTLINE}`,
                },
              },
            },
            {
              props: {
                variant: "outlined",
              },
              style: {
                paddingInline: `${BUTTON_INLINE_PADDING - 1}px`,
              },
            },
            {
              props: {
                variant: "outlined",
                color: "secondary",
                disabled: false,
              },
              style: {
                color: C_SECONDARY,
              },
            },
            {
              props: {
                variant: "outlined",
                disabled: true,
              },
              style: {
                border: `1px solid ${convertHexToRGBA(C_ON_SURFACE, 0.12)}`,
                paddingBlock: calculateButtonVerticalPadding(
                  BUTTON_SIZES.MEDIUM - TOTAL_BORDER_WIDTH,
                  BUTTON_LINE_HEIGHT,
                ),
                color: convertHexToRGBA(C_ON_SURFACE, 0.38),
                background: "transparent",
              },
            },
          ],
        },
        sizeLarge: {
          paddingBlock: calculateButtonVerticalPadding(BUTTON_SIZES.LARGE, BUTTON_LINE_HEIGHT),
        },
        sizeSmall: {
          paddingBlock: calculateButtonVerticalPadding(BUTTON_SIZES.SMALL, BUTTON_LINE_HEIGHT),
        },
        outlined: {
          "&.MuiButton-sizeLarge": {
            paddingBlock: calculateButtonVerticalPadding(BUTTON_SIZES.LARGE - TOTAL_BORDER_WIDTH, BUTTON_LINE_HEIGHT),
          },
          "&.MuiButton-sizeSmall": {
            paddingBlock: calculateButtonVerticalPadding(BUTTON_SIZES.SMALL - TOTAL_BORDER_WIDTH, BUTTON_LINE_HEIGHT),
          },
        },
        startIcon: {
          marginLeft: "-8px",
          "> *": {
            fontSize: "24px !important",
            marginBlock: "-2px",
          },
        },
        endIcon: {
          marginRight: "-8px",
          "> *": {
            fontSize: "24px !important",
            marginBlock: "-2px",
          },
        },
        text: {
          variants: [
            {
              props: {
                variant: "text",
                color: "textPrimary",
              },
              style: {
                color: C_ON_SURFACE,

                ["&:hover:not(.Mui-disabled)"]: {
                  backgroundColor: convertHexToRGBA(C_ON_SURFACE, 0.08),
                },
              },
            },
            {
              props: {
                variant: "text",
                color: "textSecondary",
              },
              style: {
                color: C_ON_SURFACE_VARIANT,

                ["&:hover:not(.Mui-disabled)"]: {
                  backgroundColor: convertHexToRGBA(C_ON_SURFACE_VARIANT, 0.08),
                },
              },
            },
            {
              props: {
                variant: "text",
                color: "textLabel",
              },
              style: {
                color: C_ON_SURFACE_LOW_EMPHASIS,

                ["&:hover:not(.Mui-disabled)"]: {
                  backgroundColor: convertHexToRGBA(C_ON_SURFACE_LOW_EMPHASIS, 0.08),
                },
              },
            },
          ],

          ["&:hover:not(.Mui-disabled)"]: {
            background: convertHexToRGBA(C_PRIMARY, 0.08),
          },
          ["&.MuiButton-textError:hover"]: {
            backgroundColor: convertHexToRGBA(C_ERROR, 0.08),
          },
          ["&.Mui-disabled"]: {
            color: convertHexToRGBA(C_ON_SURFACE, 0.38),
          },
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          ...getFontStyle(FontSize.LabelLarge),
          borderRadius: CORNER_EXTRA_LARGE,
          color: C_ON_SURFACE,
          borderColor: C_OUTLINE,

          ["&.Mui-selected"]: {
            color: C_ON_SECONDARY_CONTAINER,
            background: C_SECONDARY_CONTAINER,
            borderColor: C_OUTLINE,
          },
        },
        sizeLarge: {
          paddingBlock: calculateButtonVerticalPadding(BUTTON_SIZES.LARGE - TOTAL_BORDER_WIDTH, BUTTON_LINE_HEIGHT),
        },
        sizeMedium: {
          paddingBlock: calculateButtonVerticalPadding(BUTTON_SIZES.MEDIUM - TOTAL_BORDER_WIDTH, BUTTON_LINE_HEIGHT),
        },
        sizeSmall: {
          paddingBlock: calculateButtonVerticalPadding(BUTTON_SIZES.SMALL - TOTAL_BORDER_WIDTH, BUTTON_LINE_HEIGHT),
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: "48px",
          backgroundColor: C_SURFACE_BRIGHT,
          borderRadius: CORNER_MEDIUM,
          borderBottom: `1px solid ${C_OUTLINE_VARIANT}`,
        },

        indicator: {
          height: "3px",
          borderRadius: "1.5px",
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: "none",
          "&:hover, &:focus": {
            "&::after": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "currentColor",
              opacity: 0.08,
            },
          },
        },
      },
    },
    MuiIconButton: {
      defaultProps: {
        variant: "default",
      },
      styleOverrides: {
        root: {
          position: "relative",
          ["&:after"]: {
            content: '""',
            position: "absolute",
            inset: 0,
            borderRadius: "inherit",
            background: "currentColor",
            transition: "opacity 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
            opacity: 0,
          },
          ["&:hover&:after"]: {
            opacity: 0.08,
          },
          variants: [
            {
              props: {
                variant: "default",
                color: "primary",
              },
              style: {
                color: C_PRIMARY,
              },
            },
            {
              props: {
                variant: "default",
                color: "secondary",
              },
              style: {
                color: C_ON_SECONDARY_CONTAINER,
              },
            },
            {
              props: {
                variant: "default",
                disabled: true,
              },
              style: {
                color: `${convertHexToRGBA(C_ON_SURFACE, 0.38)} !important`,
              },
            },
            {
              props: {
                variant: "contained",
                color: "primary",
              },
              style: {
                background: C_PRIMARY,
                color: C_ON_PRIMARY,

                ["&:hover"]: {
                  background: C_PRIMARY,
                  color: C_ON_PRIMARY,
                },
              },
            },
            {
              props: {
                variant: "contained",
                color: "secondary",
              },
              style: {
                background: C_SECONDARY_CONTAINER,
                color: C_ON_SECONDARY_CONTAINER,

                ["&:hover"]: {
                  background: C_SECONDARY_CONTAINER,
                  color: C_ON_SECONDARY_CONTAINER,
                },
              },
            },
            {
              props: {
                variant: "contained",
                disabled: true,
              },
              style: {
                background: `${convertHexToRGBA(C_ON_SURFACE, 0.12)} !important`,
                color: `${convertHexToRGBA(C_ON_SURFACE, 0.38)} !important`,
              },
            },
            {
              props: {
                size: "tiny",
              },
              style: {
                height: 24,
                width: 24,
                ["& svg"]: {
                  fontSize: 20,
                },
              },
            },
          ],
        },
        sizeSmall: {
          padding: calculateButtonVerticalPadding(BUTTON_SIZES.SMALL, 24),
        },
        sizeMedium: {
          padding: calculateButtonVerticalPadding(BUTTON_SIZES.MEDIUM, 24),
        },
        sizeLarge: {
          padding: calculateButtonVerticalPadding(BUTTON_SIZES.LARGE, 24),
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root.MuiOutlinedInput-root": {
            padding: `${calculateButtonVerticalPadding(INPUT_SIZES.MEDIUM, INPUT_LINE_HEIGHT)} 14px !important`,
          },
          "& .MuiOutlinedInput-root .MuiAutocomplete-input": {
            padding: "0px",
          },
          "& .MuiOutlinedInput-root.MuiOutlinedInput-root.MuiInputBase-sizeSmall": {
            paddingBlock: `${calculateButtonVerticalPadding(INPUT_SIZES.SMALL, INPUT_LINE_HEIGHT)} !important`,
          },
          "& .MuiOutlinedInput-root.MuiInputBase-sizeSmall .MuiAutocomplete-input": {
            paddingBlock: "0px",
          },
        },
        listbox: {
          "& .MuiAutocomplete-option": {
            "&:hover": {
              backgroundColor: convertHexToRGBA(C_ON_SURFACE, 0.08),
            },
            "&.Mui-selected": {
              backgroundColor: C_SECONDARY_CONTAINER,
              "&:hover": {
                backgroundColor: convertHexToRGBA(C_SECONDARY_CONTAINER, 0.8),
              },
            },
            "&.Mui-focusVisible": {
              backgroundColor: convertHexToRGBA(C_ON_SURFACE, 0.12),
            },
            "&:active": {
              backgroundColor: convertHexToRGBA(C_ON_SURFACE, 0.16),
            },
          },
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: "outlined",
      },
      styleOverrides: {
        root: {
          "& .MuiInputBase-input": {
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          },
          variants: [
            {
              props: { highlight: true },
              style: {
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: C_INFORMATION,
                },
                "& .MuiOutlinedInput-input": {
                  color: C_INFORMATION,
                },
                "& .MuiFormHelperText-root": {
                  color: C_INFORMATION,
                },
              },
            },
          ],
        },
      },
    },
    MuiSelect: {
      defaultProps: {
        variant: "outlined",
        IconComponent: ArrowDropDownIcon,
      },
    },
    MuiFormControl: {
      defaultProps: {
        variant: "outlined",
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          "& .MuiCheckbox-root": {
            alignSelf: "flex-start",
          },
        },
      },
    },
    MuiSwitch: {
      defaultProps: {
        color: "primary",
      },
    },
    MuiBreadcrumbs: {
      styleOverrides: {
        separator: {
          margin: 0,
        },
        root: {
          ...getFontStyle(FontSize.BodySmall),

          "*:not(:hover)": {
            color: C_ON_SURFACE_VARIANT,
          },
        },
        ol: {
          gap: "8px",
        },
        li: {
          marginLeft: 0,
          marginRight: 0,
          "&:last-child": {
            fontWeight: "bold",
          },
        },
      },
    },
    MuiLink: {
      defaultProps: {
        underline: "hover",
      },
    },
    // @ts-ignore
    PrivateBreadcrumbCollapsed: {
      styleOverrides: {
        root: {
          marginLeft: "-4px",
          marginRight: "-4px",
        },
      },
    },
    MuiInput: {
      styleOverrides: {
        input: {
          height: "auto",
          padding: "8px 0",
        },
        underline: {
          "&:before": {
            borderColor: C_OUTLINE_VARIANT,
            borderBottomWidth: "1px",
          },
          "&:hover:before": {
            borderColor: C_OUTLINE,
          },
          "&.Mui-error:before": {
            borderColor: C_ERROR,
          },
        },
      },
    },
    MuiSvgIcon: {
      styleOverrides: {
        fontSizeSmall: {
          fontSize: 20,
        },
        fontSizeMedium: {
          fontSize: 24,
        },
        fontSizeLarge: {
          fontSize: 40,
        },
        fontSizeExtraLarge: {
          fontSize: 48,
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paperAnchorLeft: {
          borderRadius: "0 24px 24px 0",
        },
        paperAnchorBottom: {
          borderRadius: "24px 24px 0 0",
        },
        paperAnchorTop: {
          borderRadius: "0 0 24px 24px",
        },
      },
    },
    MuiDialog: {
      defaultProps: {
        fullWidth: true,
      },
      styleOverrides: {
        paper: ({ theme }) => ({
          borderRadius: CORNER_EXTRA_LARGE,
          maxHeight: "90%",
        }),
        // @ts-expect-error Custom dialog size classes not in MUI types
        paperWidthPhone: {
          width: "100wv",
          maxWidth: "100wv",
        },
        paperWidthTablet: {
          width: "720px",
          maxWidth: "720px",
        },
        ["paperWidthDesktop-s"]: {
          width: "780px",
          maxWidth: "780px",
        },
        ["paperWidthDesktop-m"]: {
          width: "980px",
          maxWidth: "980px",
        },
        ["paperWidthDesktop-l"]: {
          width: "1200px",
          maxWidth: "1200px",
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: "16px 24px 0 24px",
          "&:last-child": {
            paddingBottom: "24px",
          },
        },
      },
    },
    MuiDialogTitle: {
      defaultProps: {
        variant: "headlineSmall",
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: "24px",
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          color: C_ON_SURFACE_LOW_EMPHASIS,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          ...getFontStyle(FontSize.BodyLarge),
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: C_PRIMARY_HOVER,
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: C_PRIMARY,
          },
          "&.Mui-error .MuiOutlinedInput-notchedOutline": {
            borderColor: C_ERROR,
          },
          variants: [
            {
              props: { size: "small" },
              style: {
                "& .MuiSelect-icon": {
                  right: "8px",
                },
              },
            },
            {
              props: { size: "medium" },
              style: {
                "& .MuiSelect-icon": {
                  right: "12px",
                },
              },
            },
          ],
        },
        inputSizeSmall: {
          paddingBlock: calculateButtonVerticalPadding(INPUT_SIZES.SMALL, INPUT_LINE_HEIGHT),
        },
        input: {
          height: "unset",
          paddingBlock: calculateButtonVerticalPadding(INPUT_SIZES.MEDIUM, INPUT_LINE_HEIGHT),
        },
        multiline: {
          paddingBlock: calculateButtonVerticalPadding(INPUT_SIZES.MEDIUM, INPUT_LINE_HEIGHT),
          "& textarea": {
            padding: 0,
          },
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: CORNER_EXTRA_LARGE,
          minWidth: "160px",
          maxWidth: "320px,",
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          ...getFontStyle(FontSize.LabelLarge),
          padding: MENU_ITEM_PADDING,
          "&:hover": {
            backgroundColor: convertHexToRGBA(C_ON_SURFACE, 0.08),
          },
          "&.Mui-focusVisible": {
            backgroundColor: convertHexToRGBA(C_ON_SURFACE, 0.12),
          },
          "&:active": {
            backgroundColor: convertHexToRGBA(C_ON_SURFACE, 0.16),
          },
          "&.Mui-selected": {
            backgroundColor: C_SECONDARY_CONTAINER,
            "&:hover": {
              backgroundColor: convertHexToRGBA(C_SECONDARY_CONTAINER, 0.8),
            },
          },
        },
      },
    },
    MuiTreeItem: {
      styleOverrides: {
        iconContainer: {
          "& svg": {
            fontSize: "24px",
          },
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          color: C_ON_SURFACE_VARIANT,
          ...getFontStyle(FontSize.TitleMedium),
        },
      },
    },
    MuiFormHelperText: {
      defaultProps: {
        variant: "outlined",
      },
      styleOverrides: {
        root: {
          ...getFontStyle(FontSize.BodySmall),
          color: C_ON_SURFACE_LOW_EMPHASIS,
          "&.Mui-disabled": {
            color: C_ON_SURFACE_LOW_EMPHASIS,
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          "&.MuiInputLabel-standard": {
            transform: "translate(0, 24px)", // Matches the extra padding added to the input
          },
          "+ .MuiInput-formControl": {
            marginTop: "16px",
          },
        },
        shrink: {
          color: C_ON_SURFACE_VARIANT,
          "&.MuiInputLabel-standard": {
            transform: "translate(0, -1.5px) scale(0.75)", // Restore default after the override above
          },
        },
      },
    },
    MuiHelperText: {
      styleOverrides: {
        root: {
          marginTop: "4px",
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          ...getFontStyle(FontSize.BodyLarge),
        },
      },
    },
    MuiPickersInputBase: {
      styleOverrides: {
        root: {
          ...getFontStyle(FontSize.BodyLarge),
        },
      },
    },
    MuiStack: {
      defaultProps: {
        useFlexGap: true,
      },
    },
    MuiList: {
      styleOverrides: {
        padding: {
          paddingTop: 8,
          paddingBottom: 8,
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          variants: [
            {
              props: { density: -2 },
              style: {
                paddingBlock: "4px",
              },
            },
            {
              props: { density: -4 },
              style: {
                paddingBlock: "0",
              },
            },
          ],
        },
      },
    },

    MuiListItemText: {
      styleOverrides: {
        root: {
          marginBlock: 0,
          minHeight: "40px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        },
      },
    },
    MuiPaper: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          borderRadius: "12px",
          "&.MuiMenu-paper, &.MuiAutocomplete-paper": {
            marginTop: "4px",
            borderRadius: "4px",
            boxShadow: ELEVATION_2,
            backgroundColor: C_SURFACE,
          },
        },
      },
    },
    MuiCard: {
      defaultProps: {
        variant: "outlined",
      },
      styleOverrides: {
        root: {
          borderRadius: "12px", // TODO - check if needed if it's applied to Paper
          flex: "none",
        },
      },
    },
    MuiCardActionArea: {
      styleOverrides: {
        root: {
          color: C_ON_SURFACE,
          ["&:hover"]: {
            opcaity: 0.08,
          },
        },
      },
    },

    MuiTypography: {
      defaultProps: {
        variantMapping: {
          // Map the new variant to render a <h1> by default
          displaySmall: "p",
          headlineLarge: "p",
          headlineMedium: "p",
          headlineSmall: "p",
          titleLarge: "p",
          titleMedium: "p",
          titleSmall: "p",
          labelLarge: "p",
          labelMedium: "p",
          labelSmall: "p",
          bodyLarge: "p",
          bodyMedium: "p",
          bodySmall: "p",
        },
        variant: "bodyMedium",
      },
    },
    MuiStepLabel: {
      styleOverrides: {
        label: {
          ...getFontStyle(FontSize.BodyMedium),
          ["&.Mui-active"]: {
            color: C_PRIMARY,
          },
        },
      },
    },
    MuiSkeleton: {
      defaultProps: {
        variant: "rounded",
        animation: "wave",
      },
      styleOverrides: {
        rounded: {
          borderRadius: "8px",
        },
      },
    },
    MuiTable: {
      styleOverrides: {
        root: {
          "&:not(.multi-body)": {
            "& > tbody > tr.MuiTableRow-root:nth-child(even)": {
              backgroundColor: C_SURFACE,
            },
          },
          "&.multi-body": {
            "& > tbody.MuiTableBody-root:nth-child(odd)": {
              backgroundColor: C_SURFACE,
            },
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          backgroundColor: "transparent",
          borderBottom: `3px solid ${C_OUTLINE_VARIANT}`,
          fontSize: "inherit",
          lineHeight: "inherit",
          color: "inherit",
          verticalAlign: "top",
          padding: "8px",
        },
        body: {
          border: "none",
          fontSize: "inherit",
          lineHeight: "inherit",
          color: "inherit",
          verticalAlign: "top",
          padding: "8px",
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          variants: [
            {
              props: { variant: "rich" },
              style: {
                padding: "12px 16px",
                backgroundColor: C_SURFACE,
                filter: "drop-shadow(0px 2px 6px rgba(0, 0, 0, 0.15)) drop-shadow(0px 1px 2px rgba(0, 0, 0, 0.3))",
                borderRadius: "12px",
                color: C_ON_SURFACE,
                pointerEvents: "auto",
              },
            },
            {
              props: { variant: "simple" },
              style: {
                padding: "8px",
                borderRadius: CORNER_EXTRA_SMALL,
                backgroundColor: C_WHITE,
                color: C_ON_SURFACE,
                border: `1px solid ${C_OUTLINE_VARIANT}`,
                ...getFontStyle(FontSize.BodySmall),
              },
            },
          ],
        },
        arrow: {
          variants: [
            {
              props: { variant: "rich" },
              style: {
                color: C_SURFACE,
              },
            },
          ],
        },
      },
    },
  },
  breakpoints: {
    values: {
      phone: 0,
      tablet: RESOLUTION_TABLET,
      "desktop-s": RESOLUTION_DESKTOP_S,
      "desktop-m": RESOLUTION_DESKTOP_M,
      "desktop-l": RESOLUTION_DESKTOP_L,
    },
  },
  palette: {
    // =========================================================================
    // BUILT-IN COLORS (with MD3 container tokens)
    // Usage: theme.palette.primary.main, theme.palette.primary.container, etc.
    // =========================================================================
    primary: {
      main: C_PRIMARY,
      contrastText: C_ON_PRIMARY,
      container: C_PRIMARY_CONTAINER,
      onContainer: C_ON_PRIMARY_CONTAINER,
    },
    secondary: {
      main: C_SECONDARY,
      contrastText: C_ON_SECONDARY,
      container: C_SECONDARY_CONTAINER,
      onContainer: C_ON_SECONDARY_CONTAINER,
    },
    error: {
      main: C_ERROR,
      contrastText: C_ON_ERROR,
      container: C_ERROR_CONTAINER,
      onContainer: C_ON_ERROR_CONTAINER,
    },
    warning: {
      main: C_WARNING,
      contrastText: C_WHITE,
      container: C_WARNING_CONTAINER,
      onContainer: C_ON_WARNING_CONTAINER,
    },
    info: {
      main: C_INFORMATION,
      contrastText: C_WHITE,
      container: C_INFORMATION_CONTAINER,
      onContainer: C_ON_INFORMATION_CONTAINER,
    },
    success: {
      main: C_SUCCESS,
      contrastText: C_WHITE,
      container: C_SUCCESS_CONTAINER,
      onContainer: C_ON_SUCCESS_CONTAINER,
    },
    text: {
      primary: C_ON_SURFACE,
      secondary: C_ON_SURFACE_VARIANT,
      label: C_ON_SURFACE_LOW_EMPHASIS,
    },
    divider: C_OUTLINE_VARIANT,

    // =========================================================================
    // CONTAINER COLOR ALIASES (for component color prop: <Button color="primaryContainer">)
    // These reference the same values as the nested tokens above.
    // =========================================================================
    primaryContainer: {
      main: C_PRIMARY_CONTAINER,
      contrastText: C_ON_PRIMARY_CONTAINER,
    },
    onPrimaryContainer: {
      main: C_ON_PRIMARY_CONTAINER,
      contrastText: C_PRIMARY_CONTAINER,
    },
    secondaryContainer: {
      main: C_SECONDARY_CONTAINER,
      contrastText: C_ON_SECONDARY_CONTAINER,
    },
    onSecondaryContainer: {
      main: C_ON_SECONDARY_CONTAINER,
      contrastText: C_SECONDARY_CONTAINER,
    },
    errorContainer: {
      main: C_ERROR_CONTAINER,
      contrastText: C_ON_ERROR_CONTAINER,
    },
    onErrorContainer: {
      main: C_ON_ERROR_CONTAINER,
      contrastText: C_ERROR_CONTAINER,
    },
    warningContainer: {
      main: C_WARNING_CONTAINER,
      contrastText: C_ON_WARNING_CONTAINER,
    },
    onWarningContainer: {
      main: C_ON_WARNING_CONTAINER,
      contrastText: C_WARNING_CONTAINER,
    },
    infoContainer: {
      main: C_INFORMATION_CONTAINER,
      contrastText: C_ON_INFORMATION_CONTAINER,
    },
    onInfoContainer: {
      main: C_ON_INFORMATION_CONTAINER,
      contrastText: C_INFORMATION_CONTAINER,
    },
    successContainer: {
      main: C_SUCCESS_CONTAINER,
      contrastText: C_ON_SUCCESS_CONTAINER,
    },
    onSuccessContainer: {
      main: C_ON_SUCCESS_CONTAINER,
      contrastText: C_SUCCESS_CONTAINER,
    },

    // =========================================================================
    // OTHER CUSTOM COLORS
    // =========================================================================
    inversePrimary: {
      main: C_INVERSE_PRIMARY,
    },

    // Surface hierarchy
    surface: {
      dim: C_SURFACE_DIM,
      main: C_SURFACE,
      bright: C_SURFACE_BRIGHT,
    },
    surfaceContainer: {
      lowest: C_SURFACE_CONTAINER_LOWEST,
      low: C_SURFACE_CONTAINER_LOW,
      main: C_SURFACE_CONTAINER,
      high: C_SURFACE_CONTAINER_HIGH,
      highest: C_SURFACE_CONTAINER_HIGHEST,
    },

    table: {
      hoverBackground: C_TABLE_HOVER_BACKGROUND,
    },

    // Surface color aliases (for component color prop)
    surfaceContainerLowest: {
      main: C_SURFACE_CONTAINER_LOWEST,
    },
    surfaceContainerLow: {
      main: C_SURFACE_CONTAINER_LOW,
    },
    surfaceContainerHigh: {
      main: C_SURFACE_CONTAINER_HIGH,
    },
    surfaceContainerHighest: {
      main: C_SURFACE_CONTAINER_HIGHEST,
    },
    textPrimary: {
      main: C_ON_SURFACE,
    },
    textSecondary: {
      main: C_ON_SURFACE_VARIANT,
    },
    textLabel: {
      main: C_ON_SURFACE_LOW_EMPHASIS,
    },
    salesCampaign: {
      main: C_SALES_CAMPAIGN,
      contrastText: C_ON_SALES_CAMPAIGN,
    },
  },
  typography: {
    h1: {
      ...getFontStyle(FontSize.HeadlineLarge),
    },
    h2: {
      ...getFontStyle(FontSize.HeadlineMedium),
    },
    h3: {
      ...getFontStyle(FontSize.HeadlineSmall),
    },
    h4: {
      ...getFontStyle(FontSize.TitleLarge),
    },
    body1: {
      ...getFontStyle(FontSize.BodyMedium),
    },
    body2: {
      ...getFontStyle(FontSize.BodyLarge),
    },
    displaySmall: {
      ...getFontStyle(FontSize.DisplaySmall),
    },
    headlineLarge: {
      ...getFontStyle(FontSize.HeadlineLarge),
    },
    headlineMedium: {
      ...getFontStyle(FontSize.HeadlineMedium),
    },
    headlineSmall: {
      ...getFontStyle(FontSize.HeadlineSmall),
    },
    titleLarge: {
      ...getFontStyle(FontSize.TitleLarge),
    },
    titleMedium: {
      ...getFontStyle(FontSize.TitleMedium),
    },
    titleSmall: {
      ...getFontStyle(FontSize.TitleSmall),
    },
    labelLarge: {
      ...getFontStyle(FontSize.LabelLarge),
    },
    labelMedium: {
      ...getFontStyle(FontSize.LabelMedium),
    },
    labelSmall: {
      ...getFontStyle(FontSize.LabelSmall),
    },
    bodyLarge: {
      ...getFontStyle(FontSize.BodyLarge),
    },
    bodyMedium: {
      ...getFontStyle(FontSize.BodyMedium),
    },
    bodySmall: {
      ...getFontStyle(FontSize.BodySmall),
    },
  },
  shadows: [
    ELEVATION_0,
    ELEVATION_1,
    ELEVATION_2,
    ELEVATION_3,
    ELEVATION_4,
    ELEVATION_5,
    ...Array(19).fill("none"),
  ] as Shadows,
});

function convertHexToRGBA(hexCode: string, opacity = 1) {
  let hex = hexCode.replace("#", "");

  if (hex.length === 3) {
    hex = `${hex[0]}${hex[0]}${hex[1]}${hex[1]}${hex[2]}${hex[2]}`;
  }

  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  /* Backward compatibility for whole number based opacity values. */
  if (opacity > 1 && opacity <= 100) {
    opacity = opacity / 100;
  }

  return `rgba(${r},${g},${b},${opacity})`;
}

function calculateButtonVerticalPadding(targetHeight: number, contentHeight: number): string {
  const padding = (targetHeight - contentHeight) / 2;
  return `${padding}px`;
}
